import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, ArrowLeft, Music, TreePine, Headphones, Brain, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORIES = [
  { id: "all", icon: Moon, labelKey: "relaxation.all" },
  { id: "music", icon: Music, labelKey: "relaxation.music" },
  { id: "nature", icon: TreePine, labelKey: "relaxation.nature" },
  { id: "asmr", icon: Headphones, labelKey: "relaxation.asmr" },
  { id: "mindfulness", icon: Brain, labelKey: "relaxation.mindfulness" },
];

const VIDEOS = [
  {
    id: "kM3FI0UDKxg",
    category: "music",
    titleKey: "relaxation.healingMeditation",
    defaultTitle: "Healing Meditation Music",
    descKey: "relaxation.healingMeditationDesc",
    defaultDesc: "30 min parantavaa meditaatiomusiikkia stressin lievitykseen",
  },
  {
    id: "2bDQZkHJd3Y",
    category: "music",
    titleKey: "relaxation.tibetanMonks",
    defaultTitle: "Tibetan Monks Mantra",
    descKey: "relaxation.tibetanMonksDesc",
    defaultDesc: "Tiibetiläisten munkkien voimallinen mantra syvään meditaatioon",
  },
  {
    id: "f2AmEcp9kg0",
    category: "nature",
    titleKey: "relaxation.whalesDolphins",
    defaultTitle: "Whales & Dolphins",
    descKey: "relaxation.whalesDolphinsDesc",
    defaultDesc: "Valaiden ja delfiinien parantavia ääniä sisäiseen rauhaan",
  },
  {
    id: "rvXCs1wBBxc",
    category: "asmr",
    titleKey: "relaxation.rainSounds",
    defaultTitle: "Rain Sounds for Sleep",
    descKey: "relaxation.rainSoundsDesc",
    defaultDesc: "Rauhoittavaa sateen ropinaa uneen vaipumiseen",
  },
  {
    id: "wGG7ciA-64E",
    category: "mindfulness",
    titleKey: "relaxation.crystalBowl",
    defaultTitle: "Crystal Bowl Sound Journey",
    descKey: "relaxation.crystalBowlDesc",
    defaultDesc: "528 Hz kristallimalja-äänimatka ahdistuksen lievitykseen",
  },
  {
    id: "dlxRn5Mj8dw",
    category: "mindfulness",
    titleKey: "relaxation.tibetanHealing",
    defaultTitle: "Tibetan Healing Sounds",
    descKey: "relaxation.tibetanHealingDesc",
    defaultDesc: "Tiibetiläisiä parantavia ääniä kehon ja mielen harmoniaan",
  },
];

export default function RelaxationPage({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [playingVideo, setPlayingVideo] = useState(null);

  const isPremium = user?.is_premium;

  const filteredVideos = activeCategory === "all"
    ? VIDEOS
    : VIDEOS.filter(v => v.category === activeCategory);

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Lock className="w-10 h-10 text-purple-300" />
          </div>
          <h1 className="text-2xl font-display text-white mb-3">
            {t('relaxation.premiumTitle') || 'Premium-ominaisuus'}
          </h1>
          <p className="text-purple-200/70 font-body mb-8">
            {t('relaxation.premiumDesc') || 'Rentoutumisosio on saatavilla Premium-tilaajille. Nauti rauhoittavasta musiikista, luontoäänistä ja mindfulness-sisällöstä.'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back') || 'Takaisin'}
            </Button>
            <Button
              onClick={() => navigate('/premium')}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600"
              data-testid="upgrade-premium-btn"
            >
              {t('relaxation.upgradePremium') || 'Hanki Premium'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-display text-white">
              {t('relaxation.title') || 'Rentoutuminen'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
            >
              <Moon className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Intro */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-display text-white mb-3">
            {t('relaxation.heading') || 'Rauhoitu ja hengitä'}
          </h2>
          <p className="text-purple-200/60 font-body max-w-lg mx-auto">
            {t('relaxation.subtitle') || 'Valmista mielesi uneen rauhoittavan musiikin, luontoäänien ja mindfulness-harjoitusten avulla.'}
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="category-filters">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-all ${
                  isActive
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                    : 'bg-white/5 text-purple-200/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {t(cat.labelKey) || cat.id}
              </button>
            );
          })}
        </div>

        {/* Video grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="video-grid">
          {filteredVideos.map(video => (
            <div
              key={video.id}
              className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-purple-500/30 transition-all hover:shadow-lg hover:shadow-purple-500/10"
              data-testid={`video-card-${video.id}`}
            >
              {/* Video embed or thumbnail */}
              <div className="aspect-video relative">
                {playingVideo === video.id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                    title={t(video.titleKey) || video.defaultTitle}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <button
                    onClick={() => setPlayingVideo(video.id)}
                    className="w-full h-full relative cursor-pointer"
                    data-testid={`play-${video.id}`}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={t(video.titleKey) || video.defaultTitle}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`; }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-purple-500/90 flex items-center justify-center shadow-lg shadow-purple-500/40 group-hover:scale-110 transition-transform">
                        <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-body px-2 py-0.5 rounded-full ${
                    video.category === 'music' ? 'bg-pink-500/20 text-pink-300' :
                    video.category === 'nature' ? 'bg-green-500/20 text-green-300' :
                    video.category === 'asmr' ? 'bg-blue-500/20 text-blue-300' :
                    'bg-purple-500/20 text-purple-300'
                  }`}>
                    {t(`relaxation.${video.category}`) || video.category}
                  </span>
                </div>
                <h3 className="text-white font-display text-base mb-1">
                  {t(video.titleKey) || video.defaultTitle}
                </h3>
                <p className="text-purple-200/50 font-body text-sm">
                  {t(video.descKey) || video.defaultDesc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
